import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Popconfirm,
  message,
  Spin,
  Modal,
  Form,
  Input,
  Select,
  Upload,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import api from "../services/api";

const { Option } = Select;

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);

  const [editingUser, setEditingUser] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [form] = Form.useForm();

  // Fetch users
  const fetchUsers = async (page = 1, pageSize = 10) => {
    setLoading(true);
    try {
      const response = await api.get("/users", { params: { page, pageSize } });
      const { data, total } = response.data;
      setUsers(data);
      setPagination((prev) => ({ ...prev, total, current: page }));
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(pagination.current, pagination.pageSize);
  }, []);

  // Delete user
  const handleDelete = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      message.success("User deleted successfully");
      fetchUsers(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error deleting user:", error);
      message.error("Failed to delete user");
    }
  };

  // Open edit modal
  const handleEdit = (user) => {
    setEditingUser(user);
    form.setFieldsValue({
      username: user.username,
      email: user.email,
      phone: user.phone,
      full_name: user.full_name,
      gender: user.gender,
      date_of_birth: user.date_of_birth,
      status: user.status,
    });
    setIsEditModalVisible(true);
  };

  // Open upload modal
  const handleOpenUploadModal = (user) => {
    setEditingUser(user);
    setIsUploadModalVisible(true);
  };

  // Upload picture
  const handleUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append("profile_pic", file);
    formData.append("user_id", editingUser.user_id);

    try {
      setUploading(true);
      const res = await api.post("/users/upload-profile", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data.success && res.data.imageUrl) {
        const { imageUrl } = res.data;

        message.success("Profile picture updated!");
        fetchUsers(pagination.current, pagination.pageSize);

        setEditingUser((prev) => ({ ...prev, profile_pic: imageUrl }));
        onSuccess(res, file);
      } else {
        onError(new Error("Invalid response"), file);
      }
    } catch (error) {
      message.error("Failed to upload");
      onError(error, file);
    } finally {
      setUploading(false);
    }
  };

  // Update user
  const handleUpdateUser = async (values) => {
    try {
      await api.put(`/users/${editingUser.user_id}`, values);
      message.success("User updated successfully");
      setIsEditModalVisible(false);
      setEditingUser(null);
      form.resetFields();
      fetchUsers(pagination.current, pagination.pageSize);
    } catch (error) {
      console.error("Error updating user:", error);
      message.error("Failed to update user");
    }
  };

  // MOBILE-FRIENDLY COLUMNS
  const columns = [
    {
      title: "Profile",
      dataIndex: "profile_pic",
      key: "profile_pic",
      fixed: "left",
      width: 120,
      render: (url, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img
            src={url || "/default-avatar.png"}
            alt="Profile"
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
            }}
          />
          <Button size="small" onClick={() => handleOpenUploadModal(record)}>
            Change
          </Button>
        </div>
      ),
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      ellipsis: true,
      width: 150,
    },
    {
      title: "Full Name",
      dataIndex: "full_name",
      key: "full_name",
      ellipsis: true,
      width: 160,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ellipsis: true,
      width: 200,
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 140,
    },
    {
      title: "Gender",
      dataIndex: "gender",
      key: "gender",
      width: 100,
    },
    {
      title: "DOB",
      dataIndex: "date_of_birth",
      key: "date_of_birth",
      width: 140,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 160,
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="link" onClick={() => handleEdit(record)}>
            Edit
          </Button>

          <Popconfirm
            title="Delete this user?"
            onConfirm={() => handleDelete(record.user_id)}
          >
            <Button type="link" danger>
              Delete
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  // Table change
  const handleTableChange = (pagination) => {
    fetchUsers(pagination.current, pagination.pageSize);
  };

  return (
    <>
      <h2>Users Management</h2>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={users}
          rowKey="user_id"
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1300 }}
          size="small"
        />
      )}

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingUser(null);
          form.resetFields();
        }}
        onOk={() => form.submit()}
        okText="Update"
        className="mobile-modal"
        style={{ top: 20 }}
        bodyStyle={{ padding: "16px" }}
      >
        <Form form={form} layout="vertical" onFinish={handleUpdateUser}>
          <Form.Item name="username" label="Username" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="email" label="Email" rules={[{ type: "email" }]}>
            <Input />
          </Form.Item>

          <Form.Item name="phone" label="Phone" rules={[{ required: true }]}>
            <Input />
          </Form.Item>

          <Form.Item name="full_name" label="Full Name">
            <Input />
          </Form.Item>

          <Form.Item name="gender" label="Gender">
            <Select placeholder="Select gender">
              <Option value="male">Male</Option>
              <Option value="female">Female</Option>
              <Option value="other">Other</Option>
            </Select>
          </Form.Item>

          <Form.Item name="date_of_birth" label="Date of Birth">
            <Input type="date" />
          </Form.Item>

          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select>
              <Option value="active">Active</Option>
              <Option value="suspended">Suspended</Option>
              <Option value="deleted">Deleted</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Upload Profile Picture Modal */}
      <Modal
        title="Update Profile Picture"
        open={isUploadModalVisible}
        onCancel={() => {
          setIsUploadModalVisible(false);
          setEditingUser(null);
        }}
        footer={null}
        className="mobile-modal"
        style={{ top: 20 }}
        bodyStyle={{ padding: "16px" }}
      >
        {editingUser && (
          <div style={{ textAlign: "center" }}>
            {editingUser.profile_pic && (
              <img
                src={editingUser.profile_pic}
                alt="Current"
                style={{
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  marginBottom: 16,
                  objectFit: "cover",
                }}
              />
            )}
            <Upload customRequest={handleUpload} showUploadList={false} accept="image/*">
              <Button icon={<UploadOutlined />} loading={uploading}>
                {uploading ? "Uploading..." : "Upload New Picture"}
              </Button>
            </Upload>
          </div>
        )}
      </Modal>

      {/* Mobile Modal Styling */}
      <style>{`
        @media (max-width: 600px) {
          .mobile-modal .ant-modal {
            width: 95% !important;
            max-width: none !important;
            margin: 0 auto;
          }
        }
      `}</style>
    </>
  );
};

export default Users;
